import { useRef, useState } from "react";

import { Alert, Flex, isNullish, Text } from "@theorgchartdev/storybook";
import {
  RSBFooter,
  RSBPrimaryButton,
  RSBSecondaryButton
} from "@/appComponents/Navigation/RightSidebar/Utils/RSBFooter";
import { GetDoc, GetWSMgr, ReRenderCanvas, SetDocDirty } from "@/Utils/StateLib";
import { RuleCompositeInput } from "@/appComponents/Blocks/Composites/RuleInput/RuleCompositeInput";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { useTopic } from "@/hooks/useTopic";
import { useTopicBlocker } from "@/hooks/useTopicBlocker";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { appstr } from "@coreapp/utils/appstr";
import { Globals } from "@coreapp/CoreApp/Globals";
import { enumMatchSubs, enumUIModes } from "@coreapp/constants";
import { QRule } from "@coreapp/CoreApp/QRule";
import { createSinglePill } from "@/appComponents/Blocks/Menus/BasicChartSearchMenu/SelectableSearchMenu";
import { BoxChip } from "@/Utils/BoxChip";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";
import LevelHandler from "@/Utils/Chart/LevelHandler";
import { MinimalFilterRuleDisplay } from "./MinimalFilterRuleDisplay";
import { enumBlockerNextAction } from "@/providers/BlockerProvider";
import { computeErrors } from "@/appComponents/Blocks/Composites/RuleInput/ruleErrors";
import { useRuleErrors } from "@/appComponents/Blocks/Composites/RuleInput/useRuleErrors";
import { TOAST_STATUS, useToast } from "@/providers/ToastProvider";

interface Props {
  filter: QRule | null;
}

const areRulesEqual = (aRule: QRule | null | undefined, bRule: QRule | null | undefined) => {
  if (isNullish(aRule) || isNullish(bRule)) {
    return isNullish(aRule) && isNullish(bRule);
  }

  return aRule.RulesMatch(bRule);
};

const BuildModeOptions = (onClick: (option: { value: boolean }) => void) => {
  return [
    { label: appstr("No filter applied"), value: null, clickHandler: onClick },
    {
      label: appstr("BasicRule"),
      value: false,
      clickHandler: onClick
    },
    {
      label: appstr("ComplexRule"),
      value: true,
      clickHandler: onClick
    }
  ];
};

export const CustomChartFilter = (props: Props) => {
  const { setModeDirty } = useRightSidebarContext();
  const { addToast }: any = useToast();
  const nav = useAppNavigate();
  const { canEditDocuments } = usePermissionCheck();
  const [rule, _setRule] = useState(props.filter);
  const [showSyncNeededAlert, setShowSyncNeededAlert] = useState(false);
  const [ruleInputKey, setRuleInputKey] = useState(0);
  const { errors: ruleErrors, validate: validateRule } = useRuleErrors(rule);
  const resetRuleRef = useRef<{ restoredFlag: boolean; rule: QRule | null } | null>(null);
  if (isNullish(resetRuleRef.current)) {
    resetRuleRef.current = {
      restoredFlag: false,
      rule: props.filter?.Clone() ?? null
    };
  }

  const baseClass = "oc-chart-scope-filter-panel";

  const setRule = (nextRule: QRule | null) => {
    setModeDirty(!areRulesEqual(props.filter, nextRule));
    _setRule(nextRule);
  };

  const shouldBlock = () => {
    const isDirty = !areRulesEqual(props.filter, rule);
    const hasErrors = rule ? computeErrors(rule).hasError : false;

    return {
      doBlock: isDirty,
      alertParams: {
        title: appstr("Do you want to save changes to Filters"),
        message: hasErrors ? appstr("Errors in form") : appstr("saveconfirm3"),
        confirmText: hasErrors ? appstr("Resolve") : appstr("Save Changes"),
        denyText: appstr("Discard Changes"),
        onConfirm: hasErrors
          ? () => enumBlockerNextAction.Cancel
          : () => {
              handleSave();
            }
      }
    };
  };

  const getTopBox = () => {
    const doc = GetDoc();
    if (!doc) return null;

    // Note: No Coheads support
    const topboxID = doc.GetIWizData()?.GetTopBoxIDs()?.[0];
    if (!topboxID) return null;

    const hierarchyClass = doc.GetHierarchyClass();
    const topboxObject = hierarchyClass?.FindObject(hierarchyClass.GetRecordIDFieldDef(), topboxID);
    if (!topboxObject) return null;

    return BoxChip.FromObjDBObject(topboxObject).label();
  };

  const getLevel = () => {
    const doc = GetDoc();
    if (!doc) return -1;

    const levels = doc.GetIWizData()?.GetChartLevels();
    if (isNullish(levels)) return -1;

    return levels;
  };

  useTopicBlocker("refreshDocument", shouldBlock);
  useTopicBlocker("rightSidebarChangeMode", shouldBlock);

  useTopic("restoredDoc", (doc: QXDoc) => {
    if (resetRuleRef.current === null) return;

    resetRuleRef.current.restoredFlag = true;
    setShowSyncNeededAlert(false);
    setRule(resetRuleRef.current.rule?.Clone() ?? null);
    doc.GetIWizData()?.SetFilterRule(resetRuleRef.current.rule?.Clone() ?? null);
  });

  useTopic("chartDocumentLoaded", (doc: QXDoc) => {
    if (!doc || Globals._publishRef || resetRuleRef.current === null) return;

    setShowSyncNeededAlert(false);
    setRuleInputKey(k => k + 1);

    // If restore flag is set, the reset rule has been set already
    if (resetRuleRef.current.restoredFlag) {
      resetRuleRef.current.restoredFlag = false;
      return;
    }

    const filterRule = doc?.GetIWizData()?.GetFilterRule();
    if (isNullish(filterRule)) {
      _setRule(null);
      setModeDirty(false);
      resetRuleRef.current.rule = null;
      return;
    }

    _setRule(filterRule);
    setModeDirty(false);
    resetRuleRef.current.rule = filterRule?.Clone();
  });

  const createDefaultRule = (): QRule => {
    const newRule = new QRule();
    newRule.InitRule(false);
    newRule.SetMatchSubs(enumMatchSubs.matchonly);
    newRule.AddRulePair("", "", "", "equals", "");
    return newRule;
  };

  const handleSave = () => {
    if (!validateRule()) return;

    const wsMgr = GetWSMgr();
    const docMgr = wsMgr?.GetDocMgr();
    const iWizData = docMgr?.GetIWizDataInteractive();
    iWizData?.SetFilterRule(rule);

    if (resetRuleRef.current !== null) {
      resetRuleRef.current.rule = rule?.Clone() ?? null;
    }

    if (!areRulesEqual(props.filter, rule)) {
      setShowSyncNeededAlert(true);
    }

    setModeDirty(false);
    SetDocDirty();
    addToast({
      message: appstr("Chart scope changes saved"),
      status: TOAST_STATUS.SUCCESS,
      timeoutMS: 5000
    });

    // This call updates props.filter which will
    // update the `isDirty` flag
    ReRenderCanvas();
  };

  const handleReset = () => {
    setModeDirty(false);

    if (resetRuleRef.current === null) return;
    _setRule(resetRuleRef.current.rule);
    setShowSyncNeededAlert(false);
  };

  const handleModeChange = ({ value }: { value: boolean | null }) => {
    if (value === null) {
      setRule(null);
      return;
    }

    let nextRule = rule?.Clone();
    if (isNullish(nextRule)) {
      nextRule = createDefaultRule();
    }

    nextRule.SetIsComplex(value);
    setRule(nextRule);
  };

  const isDirty = !areRulesEqual(props.filter, rule);
  const isEditing = Globals._uiConfig.GetUIMode() === enumUIModes.edit;
  const handleStartEdit = () => {
    // Mode transitions go through the URL. setMode writes ?mode=edit on
    // the current resource route; useDirtyBlocker's location effect
    // picks up the change and drives enterMode(edit) in coreapp.
    nav.setMode("edit");
  };

  let mode = null;
  if (rule) {
    mode = rule.GetIsComplex();
  }

  const topBoxLabel = getTopBox();
  const topBoxPill =
    topBoxLabel != null ? createSinglePill({ label: topBoxLabel }, undefined, false) : null;
  const level = getLevel();

  return (
    <Flex direction="column" height="100%">
      <Flex
        direction="column"
        gap="16px"
        padding="12px"
        className={`${baseClass} ocn-list-scrollbar`}
        flex="1 1 0%"
        overflow="auto"
      >
        {showSyncNeededAlert && (
          <Alert severity="warning" showIcon>
            <Text colorModifier="warning" size="small">
              {appstr("refreshmsg")}
            </Text>
          </Alert>
        )}

        {/* Description */}
        <Text size="small">
          {isEditing ? appstr("Chart scope description") : appstr("Chart scope edit cc to edit")}
        </Text>

        {/* Top Box */}
        <Flex alignItems="center" gap="8px">
          <Text size="small" className="oc-font-weight-500">
            {appstr("topbox")}:
          </Text>
          {topBoxPill}
        </Flex>

        {/* Levels */}
        <Flex alignItems="center" gap="8px">
          <Text size="small" className="oc-font-weight-500">
            {appstr("Levels under top box")}:
          </Text>
          <Text size="small">{`<${LevelHandler.getLevelString(level)}>`}</Text>
        </Flex>

        {/* Filter Rule */}
        {isEditing ? (
          <Flex direction="column" gap="12px" width="100%">
            <RuleCompositeInput
              key={ruleInputKey}
              ruleInputProps={{
                basicRuleInputProps: {
                  requiredMarker: true,
                  rulePairProps: {
                    direction: "column",
                    showFieldComparisons: true,
                    selectSize: "medium"
                  }
                },
                errorsObject: ruleErrors
              }}
              rule={rule}
              setRule={setRule}
              editing={isEditing}
              onStartEdit={canEditDocuments ? handleStartEdit : undefined}
              hoverActions={null}
              showModeLabel
              ruleDivider
              modeOptions={{
                value: mode,
                options: BuildModeOptions(handleModeChange)
              }}
            />
          </Flex>
        ) : (
          <Flex direction="column" gap="4px">
            <MinimalFilterRuleDisplay rule={rule} />
          </Flex>
        )}
      </Flex>
      {isEditing && (
        <RSBFooter>
          <RSBSecondaryButton
            onClick={handleReset}
            disabled={!isDirty}
            dataTestId="rule-input-menu-Cancel"
          />
          <RSBPrimaryButton
            onClick={handleSave}
            disabled={!isDirty}
            dataTestId="rule-input-menu-Save"
          >
            {appstr("Save changes")}
          </RSBPrimaryButton>
        </RSBFooter>
      )}
    </Flex>
  );
};
